import { Button } from "@bcc/components/ui";
import { LoadingIcon } from "@bcc/components/ui/loading-icon/loading-icon";
import { useTranslation } from "react-i18next";
import { setTourTestId } from "../tour.utils";

export interface TourLoadingCardProps {
  readonly interactive?: boolean;
  readonly onSkip: () => void;
  /** i18n key of the message; defaults to "Preparing this step…" */
  readonly messageKey?: string;
}

/**
 * Shown while the running tour waits for its target to appear (slow-loading
 * pages, or an interactive step waiting for the user), so the tour stays
 * visible instead of disappearing mid-walkthrough.
 */
export function TourLoadingCard({ onSkip, messageKey = "tour.controls.preparing", interactive = false }: Readonly<TourLoadingCardProps>) {
  const { t } = useTranslation();
  const message = t(messageKey) as string;

  return (
    <>
      <div
        data-testid={setTourTestId("loading-overlay")}
        className={`fixed inset-0 z-[60] bg-black/55 ${interactive ? "pointer-events-none" : ""}`}
        aria-hidden="true"
      />
      <dialog
        open
        aria-label={message}
        data-testid={setTourTestId("loading-card")}
        className="bg-popover text-popover-foreground pointer-events-auto fixed top-1/2 left-1/2 z-[70] -translate-x-1/2 -translate-y-1/2 space-y-3 rounded-lg border p-4 shadow-lg"
      >
        <div className="flex items-center gap-3">
          <LoadingIcon />
          <p className="text-sm">{message}</p>
        </div>
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={onSkip} testId={setTourTestId("loading-skip")}>
            {t("tour.controls.skip")}
          </Button>
        </div>
      </dialog>
    </>
  );
}
